import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Filmstrip, SelectCard } from '@/design-system'
import { getWizardFrames } from '../../catalog'
import { FRAME_NONE } from '../../types'
import { StepHint, StepLead } from '../StepCopy'

export { FRAME_NONE }

const NoneCard = styled('button', {
  shouldForwardProp: (p) => p !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: '100%',
  borderRadius: 20,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  outline: $selected ? '2px solid var(--ds-ink)' : '1px solid var(--ds-border)',
  outlineOffset: 0,
  transition: [
    'outline-color var(--ds-duration-fast) var(--ds-ease)',
    'transform var(--ds-duration-normal) var(--ds-ease)',
    'box-shadow var(--ds-duration-normal) var(--ds-ease)',
  ].join(', '),
  boxShadow: $selected ? 'var(--ds-shadow-soft)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: 'var(--ds-shadow-soft)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
}))

export interface FramePickerProps {
  value?: string | number
  onChange: (id: string | number) => void
}

export function FramePicker({ value, onChange }: FramePickerProps) {
  const frames = useMemo(() => getWizardFrames(), [])
  const noneSelected = value == null || value === FRAME_NONE
  const selected = frames.find((f) => f.id === value)

  return (
    <Box>
      <StepLead>
        Applied to every print in the room. Choose none for borderless prints.
      </StepLead>

      <Filmstrip>
        <Box sx={{ width: 148 }}>
          <NoneCard
            type='button'
            $selected={noneSelected}
            aria-pressed={noneSelected}
            onClick={() => onChange(FRAME_NONE)}
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                aspectRatio: '1 / 1',
                borderBottom: '1px solid var(--ds-border)',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: '1.5px solid var(--ds-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: 22,
                    height: 1.5,
                    bgcolor: 'var(--ds-ink-subtle)',
                    borderRadius: 999,
                    transform: 'rotate(45deg)',
                  }}
                />
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: noneSelected ? 600 : 500,
                letterSpacing: '-0.01em',
                py: 1.5,
                px: 1.5,
                width: '100%',
                textAlign: 'left',
                boxSizing: 'border-box',
              }}
            >
              None
            </Typography>
          </NoneCard>
        </Box>

        {frames.map((f) => (
          <Box key={String(f.id)} sx={{ width: 148 }}>
            <SelectCard
              title={f.name}
              imageUrl={f.thumbUrl}
              aspectRatio='1 / 1'
              selected={value === f.id}
              onClick={() => onChange(f.id)}
            />
          </Box>
        ))}
      </Filmstrip>

      <StepHint>
        {noneSelected
          ? 'No frame — prints will be borderless.'
          : `${selected?.name ?? 'Frame'} selected — applied to all prints.`}
      </StepHint>
    </Box>
  )
}
